import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Skeleton, FormControl, Select, MenuItem, TextField, CircularProgress } from '@mui/material';
import {
    ArrowLeft,
    Play,
    Pause,
    Maximize2,
    Minimize2,
    Volume2,
    VolumeX,
    Clock,
    FileImage,
    FileVideo,
    UploadCloud,
    Megaphone,
    Eye,
} from 'lucide-react';
import { toast } from 'react-toastify';

// Import components
import MediaDropzone from '../../../components/ul/MediaDropzone';
import AdsNavbar from '../AdsNavbar';
import Calling from '../../../api/Calling';
import LottieLoad from '../../../components/LotteiLoad';

// Import utilities
import {
    imageSizeExceededMessage,
    MAX_FILE_SIZE_IMAGE,
    videoSizeExceededMessage,
    videoType,
    invalidVideoMessage,
    MAX_FILE_SIZE_VIDEO
} from '../../../components/imageType';

// Shared MUI light-field styling (rounded, green focus) — mirrors the add page
const muiFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#34C759' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#34C759' },
};

const EditInHouseAds = () => {
    const [loader, setLoader] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const { id } = useParams();
    const navigate = useNavigate();
    const [formInfo, setFormInfo] = useState({
        mainTitle: '',
        subTitle: '',
        medType: 'image',
        description: '',
        callToAction: '',
        btnName: '',
        medView: '',
        view: 0,
        clickCount: 0,
        icon: '',
        adType: 'ntv',
        web_stay_time: 1
    });

    const [file, setFile] = useState(true);
    const [imageFile, setImageFile] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [iconFile, setIconFile] = useState(null);
    const [fileProcessing, setFileProcessing] = useState({
        icon: false,
        image: false,
        video: false
    });
    const [fileStatus, setFileStatus] = useState({
        icon: null,
        image: null,
        video: null
    });

    const imageType = [
        'image/png',
        'image/jpg',
        'image/jpeg',
        'image/gif',
        'application/json'
    ];
    const invalidImageMessage = "Please select a valid image file. Allowed types: 'image/png', 'image/jpg', 'image/jpeg', 'image/gif','application/json'";

    // Preview video controls
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [previewType, setPreviewType] = useState('all');
    const videoRef = useRef(null);
    const previewContainerRef = useRef(null);

    // Helper function to check if a URL is a Lottie JSON file
    const isLottieJson = (url) => {
        if (!url) return false;
        return url.endsWith('.json');
    };

    // Check if media (medView) is Lottie JSON
    const isMediaLottie = () => {
        return isLottieJson(formInfo?.medView);
    };

    // Check if icon is Lottie JSON
    const isIconLottie = () => {
        return isLottieJson(formInfo?.icon);
    };

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getInHouseAdsData(id);
            if (resp?.status) {
                const adData = {
                    ...resp?.data,
                };
                setFormInfo(adData);
            } else {
                toast.error('Failed to load ad data');
                navigate('/manage/ads/inHouseAds/add');
            }
        } catch (error) {
            console.error(error);
            toast.error('An error occurred while loading data');
            navigate('/manage/ads/inHouseAds/add');
        } finally {
            setLoader(false);
            setInitialLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        // Add event listener for fullscreen change
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(!isMuted);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement && previewContainerRef.current) {
            if (previewContainerRef.current.requestFullscreen) {
                previewContainerRef.current.requestFullscreen();
            } else if (previewContainerRef.current.webkitRequestFullscreen) {
                previewContainerRef.current.webkitRequestFullscreen();
            } else if (previewContainerRef.current.msRequestFullscreen) {
                previewContainerRef.current.msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            setIsFullscreen(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoader(true);
        try {
            const formData = new FormData();
            formData.append('id', id);
            formData.append('mainTitle', formInfo.mainTitle);
            formData.append('subTitle', formInfo.subTitle);
            formData.append('medType', formInfo.medType || 'image');
            formData.append('description', formInfo.description);
            formData.append('callToAction', formInfo.callToAction);
            formData.append('btnName', formInfo.btnName);
            formData.append('adType', formInfo.adType);
            formData.append('web_stay_time', formInfo.web_stay_time);

            if (formInfo?.medType === 'image') {
                if (imageFile) {
                    formData.append('image', imageFile);
                    formData.append('icon', imageFile);
                }
            } else {
                if (videoFile) {
                    formData.append('video', videoFile);
                }
            }

            const resp = await Calling.updateInHouseAds(formData);
            if (resp?.status) {
                toast.success(resp?.message || 'Updated successfully!');
                navigate('/manage/ads/inHouseAds/add');
            } else {
                toast.error(resp?.message || 'Update failed!');
            }
        } catch (error) {
            console.log(error);
            toast.error('An error occurred during update');
        } finally {
            setLoader(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const processedValue = type === 'number' ? Number(value) : value;
        setFormInfo({ ...formInfo, [name]: processedValue });
    };

    // Validate + stage a media file (drives the drag/drop/paste MediaDropzone). Same validation +
    // processing/status state machine as before, file-based; passing null clears (remove ✕).
    const processMediaFile = (file, name, id) => {
        const isImage = name === 'image';
        const isIcon = name === 'icon';
        const isVideo = name === 'video';

        if (!file) {
            if (isImage) setImageFile(null);
            if (isIcon) setIconFile(null);
            if (isVideo) setVideoFile(null);
            setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
            setFileStatus((prevState) => ({ ...prevState, [id]: null }));
            setFile(false);
            return;
        }

        let isValidType = false;
        let maxSize = 0;
        let errorMessage = '';

        if (isImage || isIcon) {
            isValidType = imageType.includes(file.type);
            maxSize = MAX_FILE_SIZE_IMAGE;
            errorMessage = imageSizeExceededMessage;
        } else if (isVideo) {
            isValidType = videoType.includes(file.type);
            maxSize = MAX_FILE_SIZE_VIDEO;
            errorMessage = videoSizeExceededMessage;
        }

        if (!isValidType) {
            const fileTypeError = isImage || isIcon ? invalidImageMessage : invalidVideoMessage;
            if (isImage) setImageFile(null);
            if (isIcon) setIconFile(null);
            if (isVideo) setVideoFile(null);
            setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
            setFileStatus((prevState) => ({ ...prevState, [id]: 'error' }));
            toast.error(fileTypeError);
            setFile(false);
            return;
        }

        if (file.size > maxSize) {
            toast.error(errorMessage);
            if (isImage) setImageFile(null);
            if (isIcon) setIconFile(null);
            if (isVideo) setVideoFile(null);
            setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
            setFileStatus((prevState) => ({ ...prevState, [id]: 'error' }));
            setFile(false);
            return;
        }

        setFileProcessing((prevState) => ({ ...prevState, [id]: true }));
        setFileStatus((prevState) => ({ ...prevState, [id]: null }));

        setTimeout(() => {
            setFileProcessing((prevState) => ({ ...prevState, [id]: false }));
            setFileStatus((prevState) => ({ ...prevState, [id]: 'success' }));
        }, 1000);

        if (isImage) {
            setImageFile(file);
            setFile(true);
        } else if (isIcon) {
            setIconFile(file);
            setFile(true);
        } else if (isVideo) {
            setVideoFile(file);
            setFile(true);
        }
    };

    const handleCancel = () => {
        navigate('/manage/ads/inHouseAds/add');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num;
    };

    // Initial-load skeleton (only while the record is first being fetched). Subsequent
    // saves keep the real tree mounted (the button shows its own spinner).
    if (initialLoading) {
        return (
            <div className='w-full text-main-font'>
                <div className="mb-6 flex items-center gap-3">
                    <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px' }} />
                    <div>
                        <Skeleton variant="text" width={220} height={30} />
                        <Skeleton variant="text" width={280} height={18} />
                    </div>
                </div>
                <div className='bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden'>
                    <Skeleton variant="rectangular" height={56} />
                    <div className='p-6 grid grid-cols-1 lg:grid-cols-3 gap-8'>
                        <div className='lg:col-span-2 space-y-6'>
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                                {[...Array(3)].map((_, i) => <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: '12px' }} />)}
                            </div>
                            <Skeleton variant="rounded" height={170} sx={{ borderRadius: '12px' }} />
                            <div className='flex gap-4'>
                                <Skeleton variant="rounded" width={140} height={48} sx={{ borderRadius: '12px' }} />
                                <Skeleton variant="rounded" width={120} height={48} sx={{ borderRadius: '12px' }} />
                            </div>
                        </div>
                        <div className='lg:col-span-1'>
                            <Skeleton variant="rounded" height={360} sx={{ borderRadius: '12px' }} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className='w-full text-main-font'
        >
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-main-blue/10 flex items-center justify-center">
                        <Megaphone className="text-main-blue" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">Ad Management</h1>
                        <p className="text-sm text-main-subfont">Manage and configure your advertisements</p>
                    </div>
                </div>
            </div>

            <div className='bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden'>
                <AdsNavbar selectState={'inhouseads'} />
                <hr className='border-main-border' />

                <div className='p-6'>
                    <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4'>
                        <div className="flex items-center">
                            <button
                                onClick={handleCancel}
                                className="mr-3 text-main-subfont hover:text-main-font bg-main-bg hover:bg-main-th rounded-full p-2.5 transition-colors duration-200"
                            >
                                <ArrowLeft size={16} />
                            </button>
                            <h1 className='text-xl md:text-2xl font-bold text-main-font'>Edit Advertisement</h1>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex flex-col items-end">
                                <div className="text-xs text-main-subfont">Created</div>
                                <div className="text-sm font-medium text-main-font">{formInfo.createdAt ? formatDate(formInfo.createdAt) : 'N/A'}</div>
                            </div>
                            <div className="h-8 border-l border-main-border"></div>
                            <div className="flex flex-col items-end">
                                <div className="text-xs text-main-subfont">Views</div>
                                <div className="text-sm font-medium text-main-exBlue">{formatNumber(formInfo.view || 0)}</div>
                            </div>
                            <div className="h-8 border-l border-main-border"></div>
                            <div className="flex flex-col items-end">
                                <div className="text-xs text-main-subfont">Clicks</div>
                                <div className="text-sm font-medium text-main-blue">{formatNumber(formInfo.clickCount || 0)}</div>
                            </div>
                            <div className="h-8 border-l border-main-border"></div>
                            <div className="flex flex-col items-end">
                                <div className="text-xs text-main-yellow">Timing</div>
                                <div className="text-sm font-medium text-main-blue">{formatNumber(formInfo.web_stay_time || 0)}s</div>
                            </div>
                            <div className="h-8 border-l border-main-border"></div>
                            {/* Media Type Badge */}
                            <div className="flex flex-col items-end">
                                <div className="text-xs text-main-subfont">Media</div>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${formInfo?.adType === 'intent'
                                    ? 'bg-main-th text-main-blue'
                                    : isMediaLottie()
                                        ? 'bg-main-th text-main-blue'
                                        : formInfo?.medType === 'image'
                                            ? 'bg-main-exBlue/10 text-main-exBlue'
                                            : 'bg-purple-100 text-purple-800'
                                    }`}>
                                    {formInfo?.adType === 'intent' ? 'url' : isMediaLottie() ? 'Lottie' : formInfo?.medType}
                                </span>
                            </div>
                        </div>
                    </div>

                    <hr className='border-main-border mb-6' />

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Form Section - 2/3 width on large screens */}
                        <div className="lg:col-span-2">
                            <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-6">
                                {/* CTA, Button Name, and Web Stay Time */}
                                <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                                    {/* Type */}
                                    {formInfo.adType !== 'intent' &&
                                        <div className='flex flex-col'>
                                            <label className='text-sm font-medium text-main-font mb-2 flex items-center gap-2' htmlFor='medType'>
                                                {formInfo.medType === 'image' ? (
                                                    <FileImage className="text-main-exBlue" size={16} />
                                                ) : (
                                                    <FileVideo className="text-main-red" size={16} />
                                                )}
                                                Media Type
                                            </label>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    id='medType'
                                                    name='medType'
                                                    value={formInfo.medType}
                                                    onChange={handleChange}
                                                    sx={muiFieldSx}
                                                >
                                                    <MenuItem value="image">Image</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </div>
                                    }
                                    <div className='flex flex-col'>
                                        <label className='text-sm font-medium text-main-font mb-2 flex items-center gap-2' htmlFor='adType'>
                                            <FileImage className="text-main-exBlue" size={16} />
                                            Ad Type
                                        </label>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                id='adType'
                                                name='adType'
                                                value={formInfo.adType}
                                                onChange={handleChange}
                                                sx={muiFieldSx}
                                            >
                                                <MenuItem value="fs">Full Screen</MenuItem>
                                                <MenuItem value="ntv">Native</MenuItem>
                                                <MenuItem value="banner">Banner</MenuItem>
                                                <MenuItem value="intent">Intent (URL only)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                    <div className='flex flex-col'>
                                        <label className='text-sm font-medium text-main-font mb-2' htmlFor='callToAction'>
                                            Call to Action URL
                                        </label>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type='text'
                                            id='callToAction'
                                            name='callToAction'
                                            value={formInfo.callToAction}
                                            onChange={handleChange}
                                            placeholder='https://example.com/action'
                                            required
                                            sx={muiFieldSx}
                                        />
                                    </div>
                                </div>

                                {/* File Uploads */}
                                {formInfo.adType !== 'intent' &&
                                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                        {formInfo?.adType === 'fs' &&
                                            <div className='flex flex-col'>
                                                <label className='text-sm font-medium text-main-font mb-2 flex items-center gap-2' htmlFor='web_stay_time'>
                                                    <Clock className="text-main-yellow" size={16} />
                                                    Web Stay Time (seconds)
                                                </label>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type='number'
                                                    id='web_stay_time'
                                                    name='web_stay_time'
                                                    value={formInfo.web_stay_time}
                                                    onChange={handleChange}
                                                    placeholder='Minimum time in seconds'
                                                    required
                                                    sx={muiFieldSx}
                                                    slotProps={{ htmlInput: { min: 1, step: 1 } }}
                                                />
                                                <p className="text-xs text-main-subfont mt-1">Minimum time (in seconds) users must stay on the page</p>
                                            </div>
                                        }
                                        <div className='flex flex-col'>
                                            <label className='text-sm font-medium text-main-font mb-2' htmlFor={formInfo.medType}>
                                                {formInfo.medType === 'image' ? 'Advertisement Image / Lottie JSON' : 'Advertisement Video'}
                                            </label>
                                            <MediaDropzone
                                                file={formInfo.medType === 'image' ? imageFile : videoFile}
                                                onFile={(f) => processMediaFile(f, formInfo.medType, formInfo.medType)}
                                                accept={formInfo.medType === 'image' ? 'image/png,image/jpg,image/jpeg,image/gif,application/json' : 'video/mp4'}
                                                kind={formInfo.medType === 'image' ? 'image' : 'video'}
                                                previewUrl={formInfo.medView}
                                                processing={fileProcessing[formInfo.medType]}
                                                status={fileStatus[formInfo.medType]}
                                            />
                                            <p className="text-xs text-main-subfont mt-1">Current {isMediaLottie() ? 'Lottie animation' : formInfo.medType} will be used if no new file is selected</p>
                                        </div>
                                    </div>
                                }

                                {/* Action Buttons */}
                                <div className='flex flex-col sm:flex-row gap-4 pt-4'>
                                    <button
                                        type="submit"
                                        className="bg-main-blue hover:bg-main-blue/90 disabled:opacity-50 disabled:cursor-not-allowed text-main-white px-6 py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 font-medium shadow-sm shadow-main-blue/25"
                                        disabled={loader}
                                    >
                                        {loader ? (
                                            <CircularProgress size={18} color="inherit" />
                                        ) : (
                                            <UploadCloud size={18} />
                                        )}
                                        {loader ? 'Updating...' : 'Update Ad'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="bg-main-bg border border-main-border hover:bg-main-th text-main-font px-6 py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 font-medium"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Preview Section - 1/3 width on large screens */}
                        <div className="lg:col-span-1">
                            <div className="bg-main-bg rounded-2xl p-5 border border-main-border">
                                <h3 className="font-semibold text-main-font mb-4 flex items-center gap-2">
                                    <Eye size={18} className="text-main-blue" />
                                    Ad Preview
                                </h3>

                                {/* Preview Controls */}
                                <div className="flex mb-4 border-b border-main-border pb-2">
                                    <button
                                        className={`px-3 py-1 rounded-md text-sm font-medium mr-2 ${previewType === 'all' ? 'bg-main-th text-main-blue' : 'text-main-subfont hover:bg-main-th'}`}
                                        onClick={() => setPreviewType('all')}
                                    >
                                        All
                                    </button>
                                    <button
                                        className={`px-3 py-1 rounded-md text-sm font-medium mr-2 ${previewType === 'icon' ? 'bg-main-th text-main-blue' : 'text-main-subfont hover:bg-main-th'}`}
                                        onClick={() => setPreviewType('icon')}
                                    >
                                        Icon
                                    </button>
                                    <button
                                        className={`px-3 py-1 rounded-md text-sm font-medium ${previewType === 'media' ? 'bg-main-th text-main-blue' : 'text-main-subfont hover:bg-main-th'}`}
                                        onClick={() => setPreviewType('media')}
                                    >
                                        {isMediaLottie() ? 'Lottie' : formInfo.medType === 'image' ? 'Image' : 'Video'}
                                    </button>
                                </div>

                                {/* Preview Content */}
                                <div className="space-y-6">
                                    {/* Icon Preview */}
                                    {(previewType === 'all' || previewType === 'icon') && formInfo?.icon && (
                                        <div ref={previewContainerRef} className='bg-main-white border border-main-border p-4 rounded-xl shadow-sm'>
                                            <p className="text-sm font-medium mb-3 text-main-font flex items-center justify-between">
                                                Brand Icon
                                                {isIconLottie() && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-main-th text-main-blue">
                                                        Lottie
                                                    </span>
                                                )}
                                            </p>
                                            <div className='flex justify-center'>
                                                {isIconLottie() ? (
                                                    <div className="w-28 h-28 rounded-full border-4 border-main-border shadow-sm overflow-hidden bg-main-bg">
                                                        <LottieLoad url={formInfo?.icon} />
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={formInfo?.icon}
                                                        alt="Icon Preview"
                                                        className="w-28 h-28 object-cover rounded-full border-4 border-main-border shadow-sm"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Media Preview */}
                                    {(previewType === 'all' || previewType === 'media') && (
                                        <>
                                            {/* Lottie Preview */}
                                            {isMediaLottie() && formInfo?.medView && (
                                                <div className='bg-main-white border border-main-border p-4 rounded-xl shadow-sm'>
                                                    <p className="text-sm font-medium mb-3 text-main-font flex items-center justify-between">
                                                        Advertisement Animation
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-main-th text-main-blue">
                                                            Lottie
                                                        </span>
                                                    </p>
                                                    <div className='flex justify-center bg-main-bg rounded-md p-4'>
                                                        <div className="w-full max-h-[300px]">
                                                            <LottieLoad url={formInfo.medView} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Image Preview */}
                                            {formInfo?.medType === 'image' && formInfo?.medView && !isMediaLottie() && (
                                                <div className='bg-main-white border border-main-border p-4 rounded-xl shadow-sm'>
                                                    <p className="text-sm font-medium mb-3 text-main-font">Advertisement Image</p>
                                                    <div className='flex justify-center'>
                                                        <img
                                                            src={formInfo.medView}
                                                            alt="Ad Preview"
                                                            className="max-w-full max-h-[300px] object-contain rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Video Preview */}
                                            {formInfo?.medType === 'video' && formInfo?.medView && (
                                                <div className='bg-main-white border border-main-border p-4 rounded-xl shadow-sm'>
                                                    <p className="text-sm font-medium mb-3 text-main-font">Advertisement Video</p>
                                                    <div className='flex justify-center relative bg-black rounded-md'>
                                                        <video
                                                            ref={videoRef}
                                                            src={formInfo?.medView}
                                                            className="max-w-full max-h-[300px] rounded-md"
                                                            onClick={togglePlay}
                                                            onPlay={() => setIsPlaying(true)}
                                                            onPause={() => setIsPlaying(false)}
                                                            onEnded={() => setIsPlaying(false)}
                                                        />

                                                        {/* Video Controls */}
                                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 flex items-center">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    togglePlay();
                                                                }}
                                                                className="text-white p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                                                            >
                                                                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                                                            </button>

                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleMute();
                                                                }}
                                                                className="text-white p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors duration-200 ml-2"
                                                            >
                                                                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                                                            </button>

                                                            <div className="flex-grow"></div>

                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleFullscreen();
                                                                }}
                                                                className="text-white p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
                                                            >
                                                                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                                                            </button>
                                                        </div>

                                                        {/* Play Overlay */}
                                                        {!isPlaying && (
                                                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                                                <div className="bg-white bg-opacity-80 rounded-full p-3">
                                                                    <Play className="text-gray-800" size={20} />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Full Ad Preview */}
                                    {previewType === 'all' && (
                                        <div className='bg-main-white border border-main-border p-4 rounded-xl shadow-sm mt-4'>
                                            <p className="text-sm font-medium mb-3 text-main-font">Complete Ad Preview</p>

                                            <div className="p-4 border border-main-border rounded-xl bg-main-bg">
                                                <div className="flex items-center mb-3">
                                                    {formInfo?.icon && (
                                                        <div className="w-10 h-10 rounded-full mr-3 overflow-hidden bg-main-th">
                                                            {isIconLottie() ? (
                                                                <LottieLoad url={formInfo.icon} />
                                                            ) : (
                                                                <img
                                                                    src={formInfo.icon}
                                                                    alt="Brand Icon"
                                                                    className="w-10 h-10 rounded-full object-cover"
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <h4 className="font-medium text-main-font">{formInfo.mainTitle || 'Ad Title'}</h4>
                                                        <p className="text-sm text-main-subfont">{formInfo.subTitle || 'Subtitle'}</p>
                                                    </div>
                                                </div>

                                                {/* Media in Full Preview */}
                                                {formInfo?.medView && (
                                                    <div className="mb-3 rounded-md overflow-hidden bg-main-th">
                                                        {isMediaLottie() ? (
                                                            <div className="w-full h-32">
                                                                <LottieLoad url={formInfo.medView} />
                                                            </div>
                                                        ) : formInfo?.medType === 'image' ? (
                                                            <img
                                                                src={formInfo.medView}
                                                                alt="Ad Media"
                                                                className="w-full h-32 object-cover"
                                                            />
                                                        ) : null}
                                                    </div>
                                                )}

                                                <p className="text-sm text-main-font mb-3">{formInfo.description || 'Advertisement description will appear here.'}</p>
                                                <a
                                                    href={formInfo.callToAction}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-block bg-main-blue hover:bg-main-blue/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                                                >
                                                    {formInfo.btnName || 'Learn More'}
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default EditInHouseAds;
